import React from 'react';
import useAppStore from '../../store/useAppStore.js';

/**
 * Receipt component - renders a formatted receipt for thermal printer (80mm)
 * Expects normalized order object with items, subtotal, discount, total, and table info
 */
const Receipt = ({ order = {}, receiptItems, discount, selectedTable, tables, activeTab }) => {
  const branchReceiptData = useAppStore((state) => state.branchReceiptData);
  const receiptSettings = branchReceiptData || {};
  const shopName = receiptSettings.shopName || "ICE CREAM WORLD";
  const address = receiptSettings.address || branchReceiptData.location || "Moheni Bazar branch, Nawabshah";
  const phone = receiptSettings.phone || branchReceiptData.managerPhone || "";
  const email = receiptSettings.email;
  const website = receiptSettings.website;
  const currency = receiptSettings.currency || 'PKR';
  const footerMessage = receiptSettings.footerMessage || 'Thank you for your purchase!';
  const returnPolicy = receiptSettings.returnPolicy;
  const showTax = receiptSettings.showTaxOnReceipt ?? order.showTaxOnReceipt ?? false;
  const taxPercentage = Number(receiptSettings.taxPercentage ?? order.taxPercentage ?? 0) || 0;

  const orderTime = order.createdAt || new Date().toLocaleString();
  const items = Array.isArray(receiptItems) ? receiptItems : order.items || [];
  const subtotal = items.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);
  const discountValue = Math.min(Number(discount ?? order.discount ?? 0) || 0, subtotal);
  const tokenNo = order.token?.token_no || order.token_number || "";
  const taxAmount = order.tax !== undefined
    ? Number(order.tax)
    : showTax
      ? ((subtotal - discountValue) * taxPercentage) / 100
      : 0;
  const computedTotal = subtotal - discountValue + taxAmount;
  const rawTotal = order.total !== undefined && order.total !== null ? order.total : computedTotal;
  const total = Number(rawTotal);
  const totalValue = Number.isFinite(total) ? total : computedTotal;

  const orderType = order.order_type || order.orderType || null;
 
  const customerLabel = orderType === 'table'
    ? order.table?.name || order.table?.label || order.table_name || order.tableName || order.name || selectedTable || 'Table'
    : orderType === 'waiter'
      ? order.waiter_name || (order.waiter_id ? `Waiter ${order.waiter_id}` : 'Waiter')
      : order.customer_name || 'Walk-in';
  const rawOrderNum = order.order_number;
  const orderNumber = rawOrderNum ? `ORD-${String(rawOrderNum).padStart(6, '0')}` : 'Undefined';
  
  // Group items by token_paper
  const groupItemsByTokenPaper = (itemList) => {
    const grouped = {};
    itemList.forEach(item => {
      const paper = item.token_paper || 1;
      if (!grouped[paper]) {
        grouped[paper] = [];
      }
      grouped[paper].push(item);
    });
    return grouped;
  };

  const itemsByPaper = groupItemsByTokenPaper(items);
  const paperNumbers = Object.keys(itemsByPaper).sort((a, b) => Number(a) - Number(b));
  const hasMultiplePapers = paperNumbers.length > 1;
  
  // Only show token for walkin or waiter orders
  const shouldShowToken = (order.order_type === 'walkin' || order.order_type === 'waiter') && Boolean(tokenNo);

  return (
    <div
      className="print-area text-[10px] text-black bg-white p-2"
      style={{ width: '300px', maxWidth: '100%', paddingTop: 0 }}
    >
      {paperNumbers.map((paperNum, index) => (
        <div key={paperNum}>
          {/* Add page break before each paper except the first */}
          {index > 0 && <div style={{ pageBreakBefore: 'always' }} className="print-page-break"></div>}
          
          <div className="text-center font-bold text-sm">{shopName}</div>
          <div className='text-center'>{address}</div>
          <div className='text-center'>{phone}</div>
          {email && <div className='text-center'>{email}</div>}
          {website && <div className='text-center'>{website}</div>}

          <div className="border-b border-t border-dashed border-black py-1 my-1 text-[10px]">
            <div className="flex justify-between">
              <span>Date:</span>
              <span>{orderTime}</span>
            </div>
            <div className="flex justify-between">
              <span>Customer:</span>
              <span>{customerLabel}</span>
            </div>
            <div className='flex justify-center items-center flex-col'>
              <div className="flex gap-2 text-[12px] font-bold text-black">
                <span>Order:</span>
                <span>{orderNumber}</span>
              </div>
              {shouldShowToken && (
                <div className="flex text-[10px] font-bold text-black">
                  <span>Token No: </span>
                  <span>{tokenNo}</span>
                </div>
              )}
              {hasMultiplePapers && (
                <div className="flex text-[10px] font-bold text-blue-700 mt-1">
                  <span>Token Paper: </span>
                  <span>{paperNum}</span>
                </div>
              )}
            </div>
          </div>

          <table className="w-full border-collapse border-b py-2 border-dashed border-black text-[10px] text-black">
            <thead>
              <tr className="bg-gray-100 font-semibold text-black">
                <th className="px-1 text-left" colSpan={2}>Item</th>
                <th className="px-1 text-left">Price</th>
                <th className="px-1 text-right">Qty</th>
                <th className="px-1 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {itemsByPaper[paperNum].length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-1 py-2 text-center text-black">No items added.</td>
                </tr>
              ) : (
                itemsByPaper[paperNum].map((item) => (
                  <tr key={item.id || `${item.name}-${item.price}-${item.quantity}`}>
                    <td colSpan={2} className="px-1 lowercase capitalize">{item.name}</td>
                    <td className="px-1">{Number(item.price || 0).toFixed(2)}</td>
                    <td className="px-1 text-right">{item.quantity}</td>
                    <td className="px-1 text-right">{Number((item.price || 0) * (item.quantity || 0)).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Only show totals on the last page */}
          {index === paperNumbers.length - 1 && (
            <>
              <div className="mt-1 border-b border-dashed border-black">
                <div className="flex justify-between font-bold text-black">
                  <span>Subtotal</span>
                  <span>{currency} {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-black">
                  <span>Discount</span>
                  <span>{currency} {discountValue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-black">
                  <span>Total</span>
                  <span>{currency} {totalValue.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-2 text-center text-[10px] text-black">{footerMessage}</div>
              {returnPolicy && <div className="mt-1 text-[8px] text-black italic text-center">{returnPolicy}</div>}
              <div className='border-black border-t border-dashed mt-1'>
                <div className="text-center text-[10px] font-bold text-black pt-1">Software By Usama Khan</div>
                <div className="text-center text-[10px] font-bold text-black">+923073073104 | usama.developer40@gmail.com</div>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default Receipt;
